import { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Button,
  Tab,
  Tabs,
  Badge,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { UserOrganisation } from '../types/types';
import { useQuestionnaireTemplates } from '../hooks/useQuestionnaireTemplates';
import HumanRightsQuestionnaire from '../../src/assets/HumanRightsQuestionnaire.png';
import EnvironmentalProtection from '../../src/assets/EnvironmentalProtection.png';
import ESGDueDilligence from '../../src/assets/ESGDueDiligence.png';
import ESGDueDiligence1 from '../../src/assets/ESGDueDiligence1.png';
import ESRS from '../../src/assets/ESRS.jpg';
import CSDDDQuestionnaire from '../../src/assets/CSDDD-questionnaire.jpg';
import { useNavigate } from 'react-router-dom';
import { SendAssessmentToSupplier } from '../components/assessment/SendAssessmentToSupplier';
import QuestionnaireList from '../components/questionnaireBuilder/QuestionnaireList.tsx';
import { SentAssessmentsList } from '../components/questionnaireBuilder/SentAssessmentsList.tsx';
import { tracking } from '../util/tracking';

const SESSION_STORAGE_KEY = 'questionnairesActiveTab';

const Questionnaires = () => {
  const [activeTab, setActiveTab] = useState<string>(() => {
    // 1. Initialize state from sessionStorage
    return sessionStorage.getItem(SESSION_STORAGE_KEY) || 'default';
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const navigate = useNavigate();

  const { defaultTemplates, loading, error, getDefaultTemplates } =
    useQuestionnaireTemplates();

  const templateImages: Record<string, string> = {
    human_rights_labour: HumanRightsQuestionnaire,
    env_protection: EnvironmentalProtection,
    supplier_esg_report: ESGDueDilligence,
    supplier_lksg_report: ESGDueDiligence1,
    supplier_csddd_report: CSDDDQuestionnaire,
    esrs_vsme_2026: ESRS,
    // Add more templates as needed
  };

  const handleSendToSupplier = (templateId: string) => {
    tracking.log('UI', 'questionnaire_send_open', {
      templateId,
      page: 'questionnaires',
    });
    setSelectedTemplate(templateId);
    setShowModal(true);
  };

  // Fetch default templates on mount
  useEffect(() => {
    getDefaultTemplates();
  }, [getDefaultTemplates]);

  if (loading)
    return (
      <Container fluid className="d-flex">
        <Row className="justify-content-center align-self-center w-100">
          <Col className="text-center">
            <Spinner animation="border" role="status" variant="primary" />
          </Col>
        </Row>
      </Container>
    );

  if (error)
    return (
      <Container fluid className="d-flex">
        <Row className="justify-content-center align-self-center w-100">
          <Col className="text-center">
            <p>Error: {error.message}</p>
          </Col>
        </Row>
      </Container>
    );

  return (
    <Container fluid>
      {selectedOrganisation && (
        <h2 className="mb-3">
          Questionnaires for {selectedOrganisation?.organisation?.name}
        </h2>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => {
          if (k) {
            setActiveTab(k);
            sessionStorage.setItem(SESSION_STORAGE_KEY, k);
          }
        }}
        className="mb-3"
      >
        <Tab eventKey="default" title="Default">
          <Row xs={1} sm={2} md={3} lg={4} className="g-4">
            {defaultTemplates.map((template) => {
              const imageSrc = template.key
                ? templateImages[template.key] || HumanRightsQuestionnaire
                : HumanRightsQuestionnaire;

              return (
                <Col key={template.id}>
                  <Card className="h-100" key={`card-${template.id}`}>
                    <Card.Img
                      variant="top"
                      src={imageSrc}
                      alt={template.title}
                    />
                    <Card.Body>
                      <Card.Title>{template.title}</Card.Title>
                      {template.description && (
                        <Card.Text>{template.description}</Card.Text>
                      )}
                      <p>Questions:{template.questionCount}</p>
                      {template.tags?.map((t) => (
                        <Badge key={t} bg="info">
                          {t}
                        </Badge>
                      ))}
                      {/* <small className="text-muted">
                        Version {template.version}
                      </small> */}
                    </Card.Body>
                    <Card.Footer className="d-flex justify-content-space-between">
                      <Button
                        variant="link"
                        onClick={() => {
                          navigate(`/questionnaire/${template.id}`);
                        }}
                      >
                        View
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className="ms-auto"
                        onClick={() => {
                          handleSendToSupplier(template.id);
                        }}
                      >
                        Send <i className="bi bi-arrow-right-circle"></i>
                      </Button>
                    </Card.Footer>
                  </Card>
                </Col>
              );
            })}
            {defaultTemplates.length === 0 && (
              <Col>
                <p>No default templates available.</p>
              </Col>
            )}
          </Row>
        </Tab>

        <Tab eventKey="my" title="My Questionnaires">
          <QuestionnaireList
            reloadKey={0}
            onSendToSupplier={handleSendToSupplier}
          />
        </Tab>

        <Tab eventKey="sent" title="Sent">
          <SentAssessmentsList
            buyerOrgId={selectedOrganisation?.organisation?.id || ''}
          />
        </Tab>
      </Tabs>
      <SendAssessmentToSupplier
        templateId={selectedTemplate}
        show={showModal}
        onClose={() => setShowModal(false)}
      />
    </Container>
  );
};

export default Questionnaires;
